import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Box,
    Button,
    Skeleton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    useMediaQuery,
    Stack,
    Divider,
    Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { StyledTableCell, TextCell, AmountTypography, NumberTextField, StyledTableRow, TotalCell } from 'views/ConstantColors';
import { useParams } from 'react-router-dom';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';
import secureLocalStorage from 'react-secure-storage';
import TotalComparisonDialog from './TotalComparisonDialog';

// Utility Functions
const formatAmount = (amount) => parseFloat(amount || 0).toFixed(2);

const calculateRowTotal = (row) =>
    ['breakfast', 'lunch', 'dinner', 'incidental', 'miscAmt', 'accommodation']
        .reduce((sum, field) => sum + parseFloat(row[field] || '0.00'), 0)
        .toFixed(2);

const calculateTotals = (data) =>
    data.reduce(
        (totals, row) => ({
            breakfast: totals.breakfast + parseFloat(row.breakfast || 0),
            lunch: totals.lunch + parseFloat(row.lunch || 0),
            dinner: totals.dinner + parseFloat(row.dinner || 0),
            incidental: totals.incidental + parseFloat(row.incidental || 0),
            miscAmt: totals.miscAmt + parseFloat(row.miscAmt || 0),
            accommodation: totals.accommodation + parseFloat(row.accommodation || 0)
        }),
        {
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            incidental: 0,
            miscAmt: 0,
            accommodation: 0
        }
    );

const formatLineActivity = (item) => ({
    id: item.id,
    date: item.activityDate || new Date().toISOString().split('T')[0],
    breakfast: item.breakfast?.toFixed(2) || '0.00',
    lunch: item.lunch?.toFixed(2) || '0.00',
    dinner: item.dinner?.toFixed(2) || '0.00',
    incidental: item.incidental?.toFixed(2) || '0.00',
    miscDesc: item.miscellaneous || '',
    miscAmt: '0.00',
    accommodation: item.accommodation?.toFixed(2) || '0.00'
});

const formatForApi = (row) => ({
    date: row.date,
    breakfast: parseFloat(row.breakfast || 0),
    lunch: parseFloat(row.lunch || 0),
    dinner: parseFloat(row.dinner || 0),
    incidental: parseFloat(row.incidental || 0),
    miscellaneous: row.miscellaneous,
    accommodation: parseFloat(row.accommodation || 0),
    provedAccommodation: row.provedAccommodation || null
});

// Delete Confirmation Dialog
const DeleteConfirmDialog = ({ open, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this entry? This action cannot be undone.</DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={onConfirm} color="error" variant="contained">
                Delete
            </Button>
        </DialogActions>
    </Dialog>
);

DeleteConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
};

// Loading Rows Component
const LoadingRows = ({ rowCount = 3, cellCount = 10 }) =>
    Array.from({ length: rowCount }, (_, index) => (
        <StyledTableRow key={`skeleton-${index}`}>
            {Array.from({ length: cellCount }, (_, cellIndex) => (
                <TextCell key={`cell-${cellIndex}`}>
                    <Skeleton />
                </TextCell>
            ))}
        </StyledTableRow>
    ));

LoadingRows.propTypes = {
    rowCount: PropTypes.number,
    cellCount: PropTypes.number
};

// Summary Row Component
const SummaryRow = ({ data }) => {
    const totals = calculateTotals(data);
    const grandTotal = Object.values(totals).reduce((sum, value) => sum + value, 0);

    return (
        <StyledTableRow
            sx={{
                backgroundColor: 'primary.lighter',
                '& > td': {
                    borderTop: 2,
                    borderColor: 'primary.main'
                }
            }}
        >
            <TextCell>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                    Total
                </Typography>
            </TextCell>
            <TextCell align="right">
                <AmountTypography sx={{ fontWeight: 600, color: 'primary.dark' }}>${formatAmount(totals.breakfast)}</AmountTypography>
            </TextCell>
            <TextCell align="right">
                <AmountTypography sx={{ fontWeight: 600, color: 'primary.dark' }}>${formatAmount(totals.lunch)}</AmountTypography>
            </TextCell>
            <TextCell align="right">
                <AmountTypography sx={{ fontWeight: 600, color: 'primary.dark' }}>${formatAmount(totals.dinner)}</AmountTypography>
            </TextCell>
            <TextCell align="right">
                <AmountTypography sx={{ fontWeight: 600, color: 'primary.dark' }}>${formatAmount(totals.incidental)}</AmountTypography>
            </TextCell>
            <TextCell>-</TextCell>
            <TextCell align="right">
                <AmountTypography sx={{ fontWeight: 600, color: 'primary.dark' }}>${formatAmount(totals.miscAmt)}</AmountTypography>
            </TextCell>
            <TextCell align="right">
                <AmountTypography sx={{ fontWeight: 600, color: 'primary.dark' }}>${formatAmount(totals.accommodation)}</AmountTypography>
            </TextCell>
            <TotalCell align="right">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                    ${formatAmount(grandTotal)}
                </Typography>
            </TotalCell>
            <TextCell />
        </StyledTableRow>
    );
};

SummaryRow.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired
};

// Validation function
const validateRowData = (rowData) => {
    const errors = {};
    const fieldsToValidate = ['breakfast', 'lunch', 'dinner', 'incidental', 'miscAmt', 'accommodation'];

    fieldsToValidate.forEach((field) => {
        const value = rowData[field] === '' ? 0 : parseFloat(rowData[field]);
        if (Number.isNaN(value) || value < 0) {
            errors[field] = `${field} must be a non-negative number`;
        }
    });

    if (parseFloat(rowData.miscAmt || 0) > 0 && !rowData.miscDesc) {
        errors.miscDesc = 'Description is required for miscellaneous amount';
    }

    return errors;
};

// EditableRow Component
const EditableRow = ({ row, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        ...row,
        breakfast: parseFloat(row.breakfast || 0).toFixed(2),
        lunch: parseFloat(row.lunch || 0).toFixed(2),
        dinner: parseFloat(row.dinner || 0).toFixed(2),
        incidental: parseFloat(row.incidental || 0).toFixed(2),
        miscAmt: parseFloat(row.miscAmt || 0).toFixed(2),
        accommodation: parseFloat(row.accommodation || 0).toFixed(2)
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        let updatedValue = value;
        if (['breakfast', 'lunch', 'dinner', 'incidental', 'miscAmt', 'accommodation'].includes(field)) {
            updatedValue = value === '' ? '0.00' : value;
        }

        const updatedData = { ...formData, [field]: updatedValue };
        setFormData(updatedData);
        const fieldErrors = validateRowData(updatedData);
        setErrors(fieldErrors);
    };

    const handleSave = () => {
        const formattedData = {
            ...formData,
            breakfast: parseFloat(formData.breakfast || 0).toFixed(2),
            lunch: parseFloat(formData.lunch || 0).toFixed(2),
            dinner: parseFloat(formData.dinner || 0).toFixed(2),
            incidental: parseFloat(formData.incidental || 0).toFixed(2),
            miscAmt: parseFloat(formData.miscAmt || 0).toFixed(2),
            accommodation: parseFloat(formData.accommodation || 0).toFixed(2)
        };

        const validationErrors = validateRowData(formattedData);
        if (Object.keys(validationErrors).length === 0) {
            onSave(formattedData);
        } else {
            setErrors(validationErrors);
            const errorMessages = Object.values(validationErrors).join('\n');
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: errorMessages
            });
        }
    };

    return (
        <StyledTableRow>
            <TextCell>
                <NumberTextField
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    error={!!errors.date}
                    helperText={errors.date}
                />
            </TextCell>
            {['breakfast', 'lunch', 'dinner', 'incidental'].map((field) => (
                <TextCell key={field} align="right">
                    <NumberTextField
                        value={formData[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                        type="number"
                        step="0.01"
                        min="0"
                        error={!!errors[field]}
                        helperText={errors[field]}
                    />
                </TextCell>
            ))}
            <TextCell>
                <NumberTextField
                    fullWidth
                    value={formData.miscDesc}
                    onChange={(e) => handleChange('miscDesc', e.target.value)}
                    error={!!errors.miscDesc}
                    helperText={errors.miscDesc}
                />
            </TextCell>
            <TextCell align="right">
                <NumberTextField
                    value={formData.miscAmt}
                    onChange={(e) => handleChange('miscAmt', e.target.value)}
                    type="number"
                    step="0.01"
                    min="0"
                    error={!!errors.miscAmt}
                    helperText={errors.miscAmt}
                />
            </TextCell>
            <TextCell align="right">
                <NumberTextField
                    value={formData.accommodation}
                    onChange={(e) => handleChange('accommodation', e.target.value)}
                    type="number"
                    step="0.01"
                    min="0"
                    error={!!errors.accommodation}
                    helperText={errors.accommodation}
                />
            </TextCell>
            <TotalCell align="right">${formatAmount(calculateRowTotal(formData))}</TotalCell>
            <TextCell align="center">
                <Tooltip title="Save Changes">
                    <IconButton size="small" color="success" onClick={handleSave}>
                        <CheckCircleIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                    <IconButton size="small" color="error" onClick={onCancel}>
                        <CancelIcon />
                    </IconButton>
                </Tooltip>
            </TextCell>
        </StyledTableRow>
    );
};

EditableRow.propTypes = {
    row: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

// DisplayRow Component
const DisplayRow = ({ row, onEdit, onDelete }) => (
    <StyledTableRow>
        <TextCell>{row.date}</TextCell>
        {['breakfast', 'lunch', 'dinner', 'incidental'].map((field) => (
            <TextCell key={field} align="right">
                <AmountTypography>${formatAmount(row[field])}</AmountTypography>
            </TextCell>
        ))}
        <TextCell>{row.miscDesc || '-'}</TextCell>
        <TextCell align="right">
            <AmountTypography>${formatAmount(row.miscAmt)}</AmountTypography>
        </TextCell>
        <TextCell align="right">
            <AmountTypography>${formatAmount(row.accommodation)}</AmountTypography>
        </TextCell>
        <TotalCell align="right">${formatAmount(calculateRowTotal(row))}</TotalCell>
        <TextCell align="center">
            <Tooltip title="Edit Entry">
                <IconButton size="small" color="primary" onClick={() => onEdit(row.id)}>
                    <EditIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete Entry">
                <IconButton size="small" color="error" onClick={() => onDelete(row.id)}>
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </TextCell>
    </StyledTableRow>
);

DisplayRow.propTypes = {
    row: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

// Mobile components
const MobileDisplayCard = ({ row, onEdit, onDelete }) => (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {row.date}
                    </Typography>
                    <Box>
                        <IconButton size="small" color="primary" onClick={() => onEdit(row.id)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => onDelete(row.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
                <Divider />
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            Breakfast
                        </Typography>
                        <Typography variant="body2">${formatAmount(row.breakfast)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            Lunch
                        </Typography>
                        <Typography variant="body2">${formatAmount(row.lunch)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            Dinner
                        </Typography>
                        <Typography variant="body2">${formatAmount(row.dinner)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            Incidental
                        </Typography>
                        <Typography variant="body2">${formatAmount(row.incidental)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            Misc Desc
                        </Typography>
                        <Typography variant="body2">{row.miscDesc || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            Misc Amt
                        </Typography>
                        <Typography variant="body2">${formatAmount(row.miscAmt)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            Accommodation
                        </Typography>
                        <Typography variant="body2">${formatAmount(row.accommodation)}</Typography>
                    </Grid>
                </Grid>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Daily Total:
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        ${formatAmount(calculateRowTotal(row))}
                    </Typography>
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

MobileDisplayCard.propTypes = {
    row: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

const MobileEditableCard = ({ row, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        ...row,
        breakfast: parseFloat(row.breakfast || 0).toFixed(2),
        lunch: parseFloat(row.lunch || 0).toFixed(2),
        dinner: parseFloat(row.dinner || 0).toFixed(2),
        incidental: parseFloat(row.incidental || 0).toFixed(2),
        miscAmt: parseFloat(row.miscAmt || 0).toFixed(2),
        accommodation: parseFloat(row.accommodation || 0).toFixed(2)
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        let updatedValue = value;
        if (['breakfast', 'lunch', 'dinner', 'incidental', 'miscAmt', 'accommodation'].includes(field)) {
            updatedValue = value === '' ? '0.00' : value;
        }

        const updatedData = { ...formData, [field]: updatedValue };
        setFormData(updatedData);
        const fieldErrors = validateRowData(updatedData);
        setErrors(fieldErrors);
    };

    const handleSave = () => {
        const formattedData = {
            ...formData,
            breakfast: parseFloat(formData.breakfast || 0).toFixed(2),
            lunch: parseFloat(formData.lunch || 0).toFixed(2),
            dinner: parseFloat(formData.dinner || 0).toFixed(2),
            incidental: parseFloat(formData.incidental || 0).toFixed(2),
            miscAmt: parseFloat(formData.miscAmt || 0).toFixed(2),
            accommodation: parseFloat(formData.accommodation || 0).toFixed(2)
        };

        const validationErrors = validateRowData(formattedData);
        if (Object.keys(validationErrors).length === 0) {
            onSave(formattedData);
        } else {
            setErrors(validationErrors);
            const errorMessages = Object.values(validationErrors).join('\n');
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: errorMessages
            });
        }
    };

    return (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: 'primary.main', border: '2px solid' }}>
            <CardContent sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <NumberTextField
                        type="date"
                        label="Date"
                        fullWidth
                        value={formData.date}
                        onChange={(e) => handleChange('date', e.target.value)}
                        error={!!errors.date}
                        helperText={errors.date}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <NumberTextField
                                label="Breakfast"
                                value={formData.breakfast}
                                onChange={(e) => handleChange('breakfast', e.target.value)}
                                type="number"
                                fullWidth
                                error={!!errors.breakfast}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <NumberTextField
                                label="Lunch"
                                value={formData.lunch}
                                onChange={(e) => handleChange('lunch', e.target.value)}
                                type="number"
                                fullWidth
                                error={!!errors.lunch}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <NumberTextField
                                label="Dinner"
                                value={formData.dinner}
                                onChange={(e) => handleChange('dinner', e.target.value)}
                                type="number"
                                fullWidth
                                error={!!errors.dinner}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <NumberTextField
                                label="Incidental"
                                value={formData.incidental}
                                onChange={(e) => handleChange('incidental', e.target.value)}
                                type="number"
                                fullWidth
                                error={!!errors.incidental}
                            />
                        </Grid>
                    </Grid>
                    <NumberTextField
                        fullWidth
                        label="Misc Description"
                        value={formData.miscDesc}
                        onChange={(e) => handleChange('miscDesc', e.target.value)}
                        error={!!errors.miscDesc}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <NumberTextField
                                label="Misc Amount"
                                value={formData.miscAmt}
                                onChange={(e) => handleChange('miscAmt', e.target.value)}
                                type="number"
                                fullWidth
                                error={!!errors.miscAmt}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <NumberTextField
                                label="Accommodation"
                                value={formData.accommodation}
                                onChange={(e) => handleChange('accommodation', e.target.value)}
                                type="number"
                                fullWidth
                                error={!!errors.accommodation}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button variant="outlined" color="error" onClick={onCancel} startIcon={<CancelIcon />}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="success" onClick={handleSave} startIcon={<CheckCircleIcon />}>
                            Save
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

MobileEditableCard.propTypes = {
    row: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

const MobileSummaryCard = ({ data }) => {
    const totals = calculateTotals(data);
    const grandTotal = Object.values(totals).reduce((sum, value) => sum + value, 0);

    return (
        <Card sx={{ bgcolor: 'primary.lighter', borderRadius: 2, border: '1px solid', borderColor: 'primary.main', mt: 2 }}>
            <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark', mb: 2 }}>
                    OVERALL TOTALS
                </Typography>
                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Breakfast:</Typography>
                        <Typography variant="subtitle2">${formatAmount(totals.breakfast)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Lunch:</Typography>
                        <Typography variant="subtitle2">${formatAmount(totals.lunch)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Dinner:</Typography>
                        <Typography variant="subtitle2">${formatAmount(totals.dinner)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Incidental:</Typography>
                        <Typography variant="subtitle2">${formatAmount(totals.incidental)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Misc Amount:</Typography>
                        <Typography variant="subtitle2">${formatAmount(totals.miscAmt)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Accommodation:</Typography>
                        <Typography variant="subtitle2">${formatAmount(totals.accommodation)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: 'primary.main' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                            GRAND TOTAL:
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                            ${formatAmount(grandTotal)}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

MobileSummaryCard.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired
};

const AcquittalForm = ({ onNext, onBack }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { requestId } = useParams();
    const { lineActivities, getLineActivities } = useTravelRequest();
    const [expenseData, setExpenseData] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    // Add new state for comparison dialog
    const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);

    // Load data from secureLocalStorage or API
    useEffect(() => {
        const savedData = secureLocalStorage.getItem('expenseData');
        if (savedData) {
            setExpenseData(JSON.parse(savedData));
            setIsLoading(false);
        } else if (lineActivities?.length) {
            const formattedData = lineActivities.map(formatLineActivity);
            setExpenseData(formattedData);
            secureLocalStorage.setItem('expenseData', JSON.stringify(formattedData));
            setIsLoading(false);
        }
    }, [lineActivities]);

    // Fetch data from API if needed
    useEffect(() => {
        const fetchData = async () => {
            if (requestId) {
                setIsLoading(true);
                try {
                    await getLineActivities(requestId);
                } catch (err) {
                    setError('Failed to fetch activities');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        if (!secureLocalStorage.getItem('expenseData')) {
            fetchData();
        }
    }, [requestId, getLineActivities]);

    // Update local storage when data changes
    useEffect(() => {
        if (expenseData.length > 0) {
            secureLocalStorage.setItem('expenseData', JSON.stringify(expenseData));
            const formattedLines = expenseData.map(formatForApi);
            secureLocalStorage.setItem('acquittalLines', JSON.stringify(formattedLines));
        }
    }, [expenseData]);

    const handleAddNew = () => {
        const newRow = {
            id: `temp-${Date.now()}`,
            date: new Date().toISOString().split('T')[0],
            breakfast: '0.00',
            lunch: '0.00',
            dinner: '0.00',
            incidental: '0.00',
            miscDesc: '',
            miscAmt: '0.00',
            accommodation: '0.00'
        };
        setIsAddingNew(true);
        setEditingRow(newRow.id);
        setExpenseData((prev) => [newRow, ...prev]);
    };

    const handleEdit = (id) => setEditingRow(id);

    const handleDeleteClick = (id) => {
        setDeletingId(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const updatedData = expenseData.filter((row) => row.id !== deletingId);
                setExpenseData(updatedData);
                secureLocalStorage.setItem('expenseData', JSON.stringify(updatedData));
                const formattedLines = updatedData.map(formatForApi);
                secureLocalStorage.setItem('acquittalLines', JSON.stringify(formattedLines));

                Swal.fire('Deleted!', 'Your entry has been deleted.', 'success');
                setDeleteDialogOpen(false);
                setDeletingId(null);
            }
        });
    };

    const handleSave = (updatedRow) => {
        let updatedData;
        if (isAddingNew) {
            updatedData = [updatedRow, ...expenseData.filter((row) => row.id !== updatedRow.id)];
        } else {
            updatedData = expenseData.map((row) => (row.id === updatedRow.id ? updatedRow : row));
        }

        setExpenseData(updatedData);
        secureLocalStorage.setItem('expenseData', JSON.stringify(updatedData));
        const formattedLines = updatedData.map(formatForApi);
        secureLocalStorage.setItem('acquittalLines', JSON.stringify(formattedLines));

        Swal.fire({
            icon: 'success',
            title: isAddingNew ? 'Added Successfully' : 'Updated Successfully',
            text: isAddingNew ? 'New entry has been added.' : 'Entry has been updated.',
            showConfirmButton: false,
            timer: 1500
        });

        setEditingRow(null);
        setIsAddingNew(false);
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You will lose any unsaved changes!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, cancel edit'
        }).then((result) => {
            if (result.isConfirmed) {
                if (isAddingNew) {
                    setExpenseData((prev) => prev.filter((row) => row.id !== editingRow));
                    setIsAddingNew(false);
                }
                setEditingRow(null);
            }
        });
    };

    // Update handleContinue function to show the comparison dialog instead of proceeding directly
    const handleContinue = () => {
        if (!expenseData.length) {
            Swal.fire({
                icon: 'error',
                title: 'No Data',
                text: 'Please add at least one expense entry.'
            });
            return;
        }
        setComparisonDialogOpen(true);
    };

    // Handler for when user confirms from comparison dialog
    const handleProceedToNext = () => {
        setComparisonDialogOpen(false);
        onNext();
    };

    return (
        <Card elevation={3}>
            <CardHeader
                title="Trip Acquittal Form"
                action={
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        color="primary"
                        onClick={handleAddNew}
                        disabled={isAddingNew || editingRow !== null}
                    >
                        Add New Entry
                    </Button>
                }
            />
            <CardContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isMobile ? (
                    <Box>
                        {isLoading && (
                            <Stack spacing={2}>
                                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                            </Stack>
                        )}
                        {!isLoading && expenseData.length === 0 && (
                            <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                No data available. Click &quot;Add New Entry&quot; to begin.
                            </Typography>
                        )}
                        {!isLoading &&
                            expenseData.length > 0 &&
                            expenseData.map((row) =>
                                editingRow === row.id ? (
                                    <MobileEditableCard key={row.id} row={row} onSave={handleSave} onCancel={handleCancel} />
                                ) : (
                                    <MobileDisplayCard key={row.id} row={row} onEdit={handleEdit} onDelete={handleDeleteClick} />
                                )
                            )}
                        {/* Mobile Summary Card */}
                        {!isLoading && expenseData.length > 0 && <MobileSummaryCard data={expenseData} />}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Date</StyledTableCell>
                                    <StyledTableCell align="right">Breakfast</StyledTableCell>
                                    <StyledTableCell align="right">Lunch</StyledTableCell>
                                    <StyledTableCell align="right">Dinner</StyledTableCell>
                                    <StyledTableCell align="right">Incidental</StyledTableCell>
                                    <StyledTableCell>Misc Desc</StyledTableCell>
                                    <StyledTableCell align="right">Misc Amt</StyledTableCell>
                                    <StyledTableCell align="right">Accommodation</StyledTableCell>
                                    <StyledTableCell align="right">Total</StyledTableCell>
                                    <StyledTableCell>Actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading && <LoadingRows />}
                                {!isLoading && expenseData.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10}>No data available</TableCell>
                                    </TableRow>
                                )}
                                {!isLoading &&
                                    expenseData.length > 0 &&
                                    expenseData.map((row) =>
                                        editingRow === row.id ? (
                                            <EditableRow key={row.id} row={row} onSave={handleSave} onCancel={handleCancel} />
                                        ) : (
                                            <DisplayRow key={row.id} row={row} onEdit={handleEdit} onDelete={handleDeleteClick} />
                                        )
                                    )}
                                {/* Summary Row */}
                                {!isLoading && expenseData.length > 0 && <SummaryRow data={expenseData} />}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button variant="outlined" onClick={onBack} disabled={isLoading || isAddingNew || editingRow !== null}>
                        Back
                    </Button>
                    <Button variant="contained" onClick={handleContinue} disabled={isLoading || isAddingNew || editingRow !== null}>
                        Next
                    </Button>
                </Box>
            </CardContent>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} onConfirm={handleDeleteConfirm} />

            {/* Total Comparison Dialog */}
            {/* Total Comparison Dialog */}
            <TotalComparisonDialog
                open={comparisonDialogOpen}
                onClose={() => setComparisonDialogOpen(false)}
                onProceed={handleProceedToNext}
                requestId={requestId}
            />
        </Card>
    );
};

AcquittalForm.propTypes = {
    onNext: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired
};

export default AcquittalForm;
